import React, { useState, useEffect } from 'react';
import './InfraNodeEditor.css';

/**
 * Node Configuration Editor - Configure selected infrastructure node
 */
const InfraNodeEditor = ({ node, onUpdate, onDelete, onClose, links, nodes }) => {
  const [config, setConfig] = useState(node.config || {});

  useEffect(() => {
    setConfig(node.config || {});
  }, [node]);

  const handleChange = (field, value) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
  };

  const handleSave = () => {
    onUpdate(node.id, config);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete ${node.label}?`)) {
      onDelete(node.id);
    }
  };

  const renderFields = () => {
    switch (node.type) {
      case 'agent':
        // Get connected redirectors
        const connectedRedirectors = links
          ? links
              .filter(l => {
                const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
                const targetId = typeof l.target === 'object' ? l.target.id : l.target;
                const targetNode = nodes?.find(n => n.id === targetId);
                return sourceId === node.id && targetNode?.type === 'redirector';
              })
              .map(l => {
                const targetId = typeof l.target === 'object' ? l.target.id : l.target;
                const targetNode = nodes?.find(n => n.id === targetId);
                return {
                  ...l,
                  node: targetNode
                };
              })
              .sort((a, b) => a.priority - b.priority)
          : [];

        return (
          <>
            <div className="form-group">
              <label>Agent Name</label>
              <input
                type="text"
                value={config.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="My Agent"
              />
            </div>
            <div className="form-group">
              <label>Operating System</label>
              <select
                value={config.os || 'windows'}
                onChange={(e) => handleChange('os', e.target.value)}
              >
                <option value="windows">Windows</option>
                <option value="linux">Linux</option>
                <option value="darwin">macOS</option>
              </select>
            </div>
            <div className="form-group">
              <label>Architecture</label>
              <select
                value={config.arch || 'amd64'}
                onChange={(e) => handleChange('arch', e.target.value)}
              >
                <option value="amd64">x64 (amd64)</option>
                <option value="386">x86 (386)</option>
                <option value="arm64">ARM64</option>
              </select>
            </div>
            <div className="form-group">
              <label>Failover Strategy</label>
              <select
                value={config.failoverStrategy || 'sequential'}
                onChange={(e) => handleChange('failoverStrategy', e.target.value)}
              >
                <option value="sequential">Sequential</option>
                <option value="random">Random</option>
                <option value="health_based">Health-Based</option>
              </select>
              <small>How the agent selects between multiple redirectors</small>
            </div>

            {connectedRedirectors.length > 0 && (
              <div className="form-group">
                <label>Connected Redirectors ({connectedRedirectors.length})</label>
                <div className="redirector-list">
                  {connectedRedirectors.map((link, index) => (
                    <div key={link.id} className="redirector-item">
                      <span className={`redirector-badge ${link.linkType}`}>
                        {link.linkType === 'primary' ? '🎯 Primary' : `🔄 Backup ${link.priority}`}
                      </span>
                      <span className="redirector-name">{link.node?.label || 'Unknown'}</span>
                      <span className="redirector-protocol">{link.protocol?.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
                <small>Right-click agent → Click redirector to add backup</small>
              </div>
            )}
          </>
        );

      case 'redirector':
        return (
          <>
            <div className="form-group">
              <label>Redirector Name</label>
              <input
                type="text"
                value={config.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="My Redirector"
              />
            </div>
            <div className="form-group">
              <label>Hostname / IP</label>
              <input
                type="text"
                value={config.hostname || ''}
                onChange={(e) => handleChange('hostname', e.target.value)}
                placeholder="redirector.example.com or 1.2.3.4"
              />
              <small>Domain name or IP address of the redirector</small>
            </div>
            <div className="form-group">
              <label>Port</label>
              <input
                type="number"
                value={config.port || 443}
                onChange={(e) => handleChange('port', parseInt(e.target.value))}
                min="1"
                max="65535"
              />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select
                value={config.type || 'nginx'}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="nginx">NGINX</option>
                <option value="apache">Apache</option>
                <option value="haproxy">HAProxy</option>
                <option value="nodejs">Node.js</option>
                <option value="cdn">CDN</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="form-group">
              <label>SSH Host</label>
              <input
                type="text"
                value={config.sshHost || ''}
                onChange={(e) => handleChange('sshHost', e.target.value)}
                placeholder="Same as hostname"
              />
              <small>Leave empty to use hostname</small>
            </div>
            <div className="form-group">
              <label>SSH User</label>
              <input
                type="text"
                value={config.sshUser || 'root'}
                onChange={(e) => handleChange('sshUser', e.target.value)}
                placeholder="root"
              />
            </div>
            <div className="form-group">
              <label>SSH Key Path</label>
              <input
                type="text"
                value={config.sshKeyPath || ''}
                onChange={(e) => handleChange('sshKeyPath', e.target.value)}
                placeholder="~/.ssh/id_rsa"
              />
            </div>
          </>
        );

      case 'listener':
        return (
          <>
            <div className="form-group">
              <label>Listener Name</label>
              <input
                type="text"
                value={config.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="My Listener"
              />
            </div>
            <div className="form-group">
              <label>Protocol</label>
              <select
                value={config.protocol || 'https'}
                onChange={(e) => handleChange('protocol', e.target.value)}
              >
                <option value="http">HTTP</option>
                <option value="https">HTTPS</option>
                <option value="dns">DNS</option>
                <option value="smb">SMB</option>
              </select>
            </div>
            <div className="form-group">
              <label>Port</label>
              <input
                type="number"
                value={config.port || 5000}
                onChange={(e) => handleChange('port', parseInt(e.target.value))}
                min="1"
                max="65535"
              />
            </div>
            {config.protocol === 'dns' && (
              <div className="form-group">
                <label>DNS Zone</label>
                <input
                  type="text"
                  value={config.dnsZone || 'c2.example.com'}
                  onChange={(e) => handleChange('dnsZone', e.target.value)}
                  placeholder="c2.example.com"
                />
              </div>
            )}
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={config.autoStart || false}
                  onChange={(e) => handleChange('autoStart', e.target.checked)}
                />
                {' '}Auto-start on server boot
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={config.autoRestart || false}
                  onChange={(e) => handleChange('autoRestart', e.target.checked)}
                />
                {' '}Auto-restart on crash
              </label>
            </div>
          </>
        );

      case 'c2':
        return (
          <div className="info-message">
            <p><strong>C2 Server</strong></p>
            <p>This represents the main C2 server (this instance).</p>
            <p>No configuration required.</p>
          </div>
        );

      default:
        return <p>Unknown node type: {node.type}</p>;
    }
  };

  return (
    <div className="infra-node-editor">
      <div className="editor-header">
        <h3>Configure {node.type.charAt(0).toUpperCase() + node.type.slice(1)}</h3>
        <button onClick={onClose} className="close-btn" title="Close">
          ✕
        </button>
      </div>
      <div className="editor-body">
        {renderFields()}
      </div>
      <div className="editor-footer">
        <button onClick={handleDelete} className="btn-delete">
          Delete
        </button>
        <div className="editor-actions">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default InfraNodeEditor;
